import React, { memo, useState, useEffect, useMemo } from "react";
import { Layout, Menu, Typography, Button, Drawer, ConfigProvider } from "antd";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../../redux/auth/authSlice";

import { ReactComponent as Logo } from "../../../../assets/svgs/sidebarlogo.svg";
import { postApi } from "../../../../api/api_instance";
import { API_URLS } from "../../../../api/apiUrls";

import { AiOutlineAppstore } from "react-icons/ai";
import { MdOutlineDashboardCustomize } from "react-icons/md";
import { CgSupport } from "react-icons/cg";
import { FaCalculator } from "react-icons/fa6";
import { GrFormCalendar } from "react-icons/gr";
import { GoQuestion } from "react-icons/go";
import { RiUserCommunityLine, RiOrganizationChart } from "react-icons/ri";
import { FaUser } from "react-icons/fa";
import { FiLogOut } from "react-icons/fi";

// Import the Organization Sidebar CSS for exact same styling
import "../../../Organization/components/Sidebar/sidebar.css";

const { Sider } = Layout;

// Menu Items Configuration (Employee Base)
const EMPLOYEE_MENU_ITEMS = [
  {
    key: "/employee/home",
    label: "Dashboard",
    icon: <AiOutlineAppstore className="w-5 h-5" />,
  },
  {
    key: "/employee/form-assigned",
    label: "Assigned Form",
    icon: <GrFormCalendar className="w-5 h-5" />,
  },
  {
    key: "/employee/manage-form",
    label: "Manage Form",
    icon: <GrFormCalendar className="w-5 h-5" />,
  },
  // {
  //   key: "/employee/individual-footprint-dashboard",
  //   label: "Individual footprint calculator",
  //   icon: <FaCalculator className="w-4 h-4" />,
  // },
  {
    key: "/employee/profile",
    label: "My Profile",
    icon: <FaUser className="w-5 h-5" />,
  },
  {
    key: "logout",
    label: "Log out",
    icon: <FiLogOut className="w-5 h-5" />,
  },
];

const ORG_MENU_ITEMS = [
  {
    key: "/organization/dashboard",
    label: "Dashboard",
    icon: <AiOutlineAppstore className="w-5 h-5" />,
    children: [
      {
        key: "/organization/dashboard/emission-tracking",
        label: "Emission Tracking",
      },
      {
        key: "/organization/dashboard/environmental-impact",
        label: "Environmental Impact",
      },
      { key: "/organization/dashboard/social", label: "Social" },
      { key: "/organization/dashboard/governance", label: "Governance" },
      { key: "/organization/dashboard/supply-chain", label: "Supply Chain" },
      { key: "/organization/dashboard/sdg", label: "SDG" },
    ],
  },

  {
    key: "/organization/user-management",
    label: "User Management",
    icon: <RiUserCommunityLine className="w-5 h-5" />,
    children: [
      {
        key: "/organization/user-management/roles-and-permissions",
        label: "Roles and Permissions",
      },
      { key: "/organization/user-management/profile", label: "My Profile" },
      {
        key: "/organization/user-management/license-access",
        label: "License & Access",
      },
      {
        key: "/organization/user-management/location-user",
        label: "Location User",
      },
    ],
  },
  {
    key: "/organization/projects",
    label: "Project",
    icon: <MdOutlineDashboardCustomize className="w-5 h-5" />,
    children: [
      {
        key: "/organization/projects/standard-forms",
        label: "Standard Form",
      },
      {
        key: "/organization/projects/dynamic-forms",
        label: "Dynamics Form",
      },
    ],
  },
  {
    key: "/organization/my-question-bank",
    label: "My Question Bank",
    icon: <GoQuestion className="w-5 h-5" />,
  },
  {
    key: "/organization/organization-setup",
    label: "Organization Setup",
    icon: <RiOrganizationChart className="w-5 h-5" />,
  },
  {
    key: "/organization/manage-forms",
    label: "Manage Forms",
    icon: <GrFormCalendar className="w-5 h-5" />,
  },
  {
    key: "/organization/assigned-form",
    label: "Assigned Form",
    icon: <GrFormCalendar className="w-5 h-5" />,
  },
  {
    key: "/organization/support",
    label: "Support",
    icon: <CgSupport className="w-5 h-5" />,
  },
];

const MODULE_KEY_MAP = {
  Dashboard: "dashbaord",
  "Emission Tracking": "emission-tracking",
  "Environmental Impact": "environmental-impact",
  Social: "social",
  Governance: "governance",
  "Supply Chain": "supply-chain",
  SDG: "sdg",

  Project: "project",
  "Standard Form": "standard-form",
  "Dynamics Form": "dynamic-forms",

  "User Management": "usermanagement",
  "Roles and Permissions": "roles-and-permissions",
  "My Profile": "profile",
  "License & Access": "license-access",
  "Location User": "location-user",

  "My Question Bank": "question-bank",
  "Organization Setup": "org-setup",
  "Manage Forms": "manage-forms",
  "Assigned Form": "assigned-form",
  Support: "support",
  "Individual footprint calculator": "footprint",
};

// Backend sometimes uses different spellings for the same module.
// Keys here are the canonical MODULE_KEY_MAP value; values are the
// alternative spellings accepted from the permissions payload.
const MODULE_KEY_ALIASES = {
  "standard-form": "standard_form",
  "license-access": "license",
};

// Pure Component for Sidebar Logo
const SidebarLogo = memo(({ isMobile }) => (
  <div className="flex justify-center items-center overflow-hidden">
    <Logo
      className="sidebarlogo block"
      style={{
        width: isMobile ? "32px" : "200px",
        height: "auto",
        display: "block",
      }}
      preserveAspectRatio="xMidYMid meet"
    />
  </div>
));

const FLATTEN_KEYS = [
  "/employee/home",
  "/employee/projects",
  "/employee/user-management",
  "/organization/dashboard",
  "/organization/projects",
  "/organization/user-management",
];

const flattenMenuItems = (items, location) => {
  return items.flatMap((item) => {
    const isActive =
      location.pathname === item.key ||
      (item.children &&
        item.children.some((child) => child.key === location.pathname)) ||
      location.pathname.startsWith(item.key);

    if (FLATTEN_KEYS.includes(item.key) && item.children) {
      return [
        {
          ...item,
          children: undefined,
          icon: React.cloneElement(item.icon, {
            style: {
              fill: isActive ? "#06513D" : "#000000",
            },
            className: "w-4 h-4",
          }),
          label: (
            <div
              className={`cursor-default px-1 py-0.5 font-medium text-xs ${
                isActive ? "text-[#06513D]" : "text-[#000000]"
              }`}
            >
              {item.label}
            </div>
          ),
        },

        ...item.children.map((child) => ({
          ...child,
          icon: (
            <span
              className={`cursor-default ml-6 font-medium text-[10px] ${
                isActive ? "text-[#06513D]" : "text-[#000000]"
              }`}
            >
              •
            </span>
          ),
        })),
      ];
    }

    return item;
  });
};

const SidebarMenu = memo(
  ({ items, onMenuClick, location, onParentClick, isMobile }) => {
    return (
      <div className="py-1">
        <ConfigProvider
          theme={{
            components: {
              Menu: {
                itemHoverBg: "#f8fafc",
                itemHoverColor: "#0f172a",
                itemSelectedBg: "transparent",
                itemSelectedColor: "#06513D",
                itemActiveBg: "transparent",
                subMenuItemBg: "transparent",
                itemHeight: 32,
                itemMarginBlock: 2,
              },
            },
          }}
        >
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            defaultOpenKeys={[
              location.pathname.split("/").slice(0, 3).join("/"),
            ]}
            onClick={onMenuClick}
            className="border-none text-xs"
            style={{ backgroundColor: "transparent", fontSize: "11px" }}
            items={items.map((item) => {
              const isParentActive =
                location.pathname === item.key ||
                (item.children &&
                  item.children.some(
                    (child) => child.key === location.pathname,
                  ));

              if (item.children) {
                return {
                  ...item,
                  style: isParentActive
                    ? {
                        backgroundColor: "rgba(6, 81, 61, 0.05)",
                        borderRadius: "8px",
                      }
                    : {},
                  label: (
                    <div
                      onClick={() => onParentClick(item.key)}
                      style={{ cursor: "pointer" }}
                      className={`text-xs py-0.5 ${
                        isParentActive
                          ? "text-[#06513D] font-semibold"
                          : "text-[#000000]"
                      }`}
                    >
                      <span>{item.label}</span>
                    </div>
                  ),
                  children: item.children.map((child) => {
                    const isActive = location.pathname === child.key;
                    return {
                      ...child,
                      label: (
                        <div
                          className={`flex items-center text-[10px] py-0.5 ${
                            isActive
                              ? "text-[#06513D] font-semibold"
                              : "text-[#000000]"
                          }`}
                        >
                          <span
                            className={`text-[10px] ${
                              isActive ? "text-[#06513D]" : "text-[#000000]"
                            }`}
                          >
                            •
                          </span>
                          <span className="text-[10px]">{child.label}</span>
                        </div>
                      ),
                    };
                  }),
                };
              }

              return {
                ...item,
                label: (
                  <div
                    className={`text-xs py-0.5 ${
                      isParentActive
                        ? "text-[#06513D] font-medium"
                        : "text-[#000000]"
                    }`}
                    onClick={() => onMenuClick({ key: item.key })}
                  >
                    <span>{item.label}</span>
                  </div>
                ),
              };
            })}
          />
        </ConfigProvider>
      </div>
    );
  },
);

// Sidebar Content Component
const SidebarContent = ({ isMobile, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);

  const dynamicMenuItems = useMemo(() => {
    let items = [...EMPLOYEE_MENU_ITEMS];
    const logoutItem = items.pop(); // Temporarily remove logout

    if (user?.subAdmin?.isSubAdmin || user?.managementView?.isManagementView) {
      const perms = [
        ...(user?.subAdmin?.permissions || []),
        ...(user?.managementView?.permissions || []),
      ];
      const MANAGEMENT_ALLOWED_KEYS = [
        "dashbaord",
        "dashboard",
        "emission-tracking",
        "environmental-impact",
        "social",
        "governance",
        "supply-chain",
        "sdg",
        "support",
      ];

      const hasAccess = (label) => {
        const moduleKey = MODULE_KEY_MAP[label];
        if (!moduleKey) return true; // allow if no module mapping

        const isManagementView = !!user?.managementView?.isManagementView;
        if (isManagementView && MANAGEMENT_ALLOWED_KEYS.includes(moduleKey)) {
          return true;
        }

        // "dashboard: write" is a backend/full-access permission and should NOT
        // expand sidebar visibility. Each sidebar item (including dashboard
        // sub-sections) must have its own explicit moduleKey entry in `perms`
        // to be shown. The "dashbaord" typo is checked only for the parent
        // "Dashboard" label itself, for backwards compatibility.
        return perms.some(
          (p) =>
            (p.module === moduleKey ||
              (moduleKey === "dashbaord" && p.module === "dashboard") ||
              p.module === MODULE_KEY_ALIASES[moduleKey]) &&
            (p.access === "read" || p.access === "write"),
        );
      };

      const filterItems = (menuList) => {
        return menuList
          .map((item) => {
            if (item.children) {
              const filteredChildren = filterItems(item.children);
              // Keep parent if parent explicitly has access OR any child has access
              if (hasAccess(item.label) || filteredChildren.length > 0) {
                return { ...item, children: filteredChildren };
              }
              return null;
            } else {
              return hasAccess(item.label) ? item : null;
            }
          })
          .filter(Boolean);
      };

      // Filter ORG_MENU_ITEMS recursively based on permissions
      const allowedOrgItems = filterItems(ORG_MENU_ITEMS);

      // Merge the allowed Org items into the Employee items.
      // If an Org item has the same label as an Employee item (e.g. "Dashboard", "Manage Form"),
      // merge its children into the existing Employee item to preserve the Employee base route (e.g. /employee/home).
      allowedOrgItems.forEach((orgItem) => {
        const existingItemIndex = items.findIndex(
          (i) => i.label === orgItem.label,
        );

        if (existingItemIndex !== -1) {
          // Merge children, preserve original employee key and icon
          items[existingItemIndex] = {
            ...items[existingItemIndex],
            children: [
              ...(items[existingItemIndex].children || []),
              ...(orgItem.children || []),
            ],
          };
        } else {
          items.push(orgItem);
        }
      });
    }

    // Ensure Dashboard is always at the absolute top
    const finalDashboardIndex = items.findIndex((i) => i.label === "Dashboard");
    if (finalDashboardIndex > 0) {
      const dashboardItem = items.splice(finalDashboardIndex, 1)[0];
      items.unshift(dashboardItem);
    }

    items.push(logoutItem); // Re-add logout
    return items;
  }, [user]);

  // Handle Menu Item Click
  const handleMenuClick = (e) => {
    if (e.key === "logout") {
      const savedOrgId = localStorage.getItem("orgId");
      navigate(`/employee/login/${savedOrgId}`);
      return;
    }

    navigate(e.key);
    if (isMobile && onClose) {
      onClose();
    }
  };

  // Handle Parent Item Click
  const handleParentClick = (key) => {
    navigate(key);
    if (isMobile && onClose) {
      onClose();
    }
  };

  // Handle Switch to Offset Marketplace
  const handleSwitchToOffsetMarketPlace = async () => {
    try {
      window.open("https://marketplace.offsetgo.earth", "_blank");
      return;
      const response = await postApi({
        apiUrl: API_URLS.CREATE_SWITCH_REQUEST,
        data: { id: "" },
      });
      console.log("response --", response);
      if (response.success) {
        const url = `${process.env.MARKET_PLACE_URL}/switch/${response.data}`;
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Error switching to marketplace", error);
    }
  };

  return (
    <div
      className="bg-white h-full flex flex-col"
      style={{ height: "100vh", overflow: "hidden" }}
    >
      {/* Sidebar Logo */}
      <div className=" py-3   ">
        <SidebarLogo isMobile={isMobile} />
      </div>

      {/* Sidebar Menu - No Scroll, Fixed Height */}
      <div
        className="flex-1 px-2 "
        className="employee-sidebar"
        style={{ overflow: "hidden", overflowY: "auto" }}
      >
        <SidebarMenu
          items={flattenMenuItems(dynamicMenuItems, location)}
          location={location}
          onMenuClick={handleMenuClick}
          onParentClick={handleParentClick}
          isMobile={isMobile}
        />
      </div>

      {/* Switch Button - Centered */}
      <div
        className={`flex-shrink-0 flex justify-center items-center py-2 px-4  ${
          isMobile ? "mb-8" : "py-2"
        }`}
      >
        <div className="bg-[#E5F3F6] rounded-lg p-2 h-24  w-full max-w-[120px] text-center">
          <Typography.Text className="text-[13px] font-medium block">
            Switch to
          </Typography.Text>
          <Typography.Text className="text-[9px] font-light block leading-tight">
            OffsetGo <br></br>MarketPlace
          </Typography.Text>
          <Button
            type="primary"
            onClick={handleSwitchToOffsetMarketPlace}
            className="bg-[#06513D] rounded-md text-[8px] text-white mt-3 h-5 min-w-[80px] hover:bg-[#06513D]"
            style={{
              fontSize: "10px",
              height: "24px",
              padding: "0 4px",
              lineHeight: "1.2",
            }}
          >
            Click here
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main EmployeeSidebar Component
const EmployeeSidebar = ({ isOpen, onClose, isMobile }) => {
  if (isMobile) {
    return (
      <Drawer
        title={null}
        placement="left"
        onClose={onClose}
        open={isOpen}
        width={280}
        bodyStyle={{ padding: 0 }}
        headerStyle={{ display: "none" }}
        className="mobile-sidebar-drawer"
      >
        <SidebarContent isMobile={true} onClose={onClose} />
      </Drawer>
    );
  }

  // Desktop Sidebar - No Scroll
  return (
    <Sider
      width={258}
      className="bg-white"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        height: "100vh",
        overflow: "hidden",
        zIndex: 1000,
        borderRight: "1px solid #f0f0f0",
      }}
    >
      <SidebarContent isMobile={false} />
    </Sider>
  );
};

export default EmployeeSidebar;
